"use client";

import * as React from "react";
import { Button, Card, Typography } from "@cappelendamm/design-system/react";
import { sanitizeHtml } from "@/promo/sanitize";
import type { BookClubItem } from "@cappelendamm/catalogue/crystallize/items/get-all-bookclubs.js";
import Image from "next/image";
import Link from "next/link";
import { ShowMoreButton } from "@cappelendamm/design-system/react";
import { ExpandableContainer } from "@/portable-text/ExpandableText/ExpandableContainer";
import { LinkPendingLabel } from "@/components/LinkPendingLabel";

const MAX_VISIBLE_VARIANTS = 5;
const DESCRIPTION_MAX_LINES = 2;
const PRICE_TAG_PLACEHOLDER = "0 kr + porto";

type VariantCardItem = BookClubItem["variants"][number];

type VariantCardListProps = {
  productId: string;
  variants: VariantCardItem[];
};

type VariantCardProps = {
  variant: VariantCardItem;
  priceTag?: string;
  href: string;
};

const PriceTag = ({
  text,
  compact = false,
}: {
  text: string;
  compact?: boolean;
}) => (
  <div
    className={
      compact
        ? "w-fit rounded-full px-3 py-1"
        : "-ml-6 rounded-r-xl py-1 pr-3 pl-6"
    }
    style={{
      background: "color-mix(in srgb, var(--cvr-button-background) 15%, white)",
    }}
  >
    <span className="text-sm leading-snug whitespace-pre-line text-gray-700">
      {text}
    </span>
  </div>
);

const VariantImage = ({ variant }: { variant: VariantCardItem }) => (
  <div className="relative h-56 w-full overflow-hidden md:h-64">
    {variant.image?.url ? (
      <Image
        src={variant.image.url}
        alt={variant.image.altText ?? variant.name}
        width={variant.image.width ?? 440}
        height={variant.image.height ?? 360}
        className="h-full w-full object-cover"
      />
    ) : (
      <div className="h-full w-full bg-gray-100" />
    )}
  </div>
);

const BookClubDescription = ({ caption }: { caption: unknown }) => {
  const content = Array.isArray(caption)
    ? caption.join("")
    : typeof caption === "string"
      ? caption
      : "";

  if (!content) return null;

  const looksLikeHtml = /<[^>]+>/.test(content);
  const sanitizedContent = looksLikeHtml ? sanitizeHtml(content) : content;
  const description = looksLikeHtml ? (
    <div
      className="text-gray-700 [&_blockquote]:border-l-2 [&_blockquote]:border-gray-200 [&_blockquote]:pl-4 [&_em]:italic [&_li]:my-0.5 [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-6 [&_p]:my-0 [&_strong]:font-semibold [&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-6"
      dangerouslySetInnerHTML={{ __html: sanitizedContent }}
    />
  ) : (
    <div className="whitespace-pre-line text-gray-700">{content}</div>
  );

  return (
    <ExpandableContainer
      maxLines={DESCRIPTION_MAX_LINES}
      truncated={description}
      showFade={false}
      buttonWrapperClassName="flex w-full justify-start pt-2"
      buttonClassName="text-brand inline-flex w-fit p-0 text-sm leading-none font-medium underline underline-offset-4"
      contentClassName="text-gray-700"
    >
      {description}
    </ExpandableContainer>
  );
};

const VariantCard = ({ variant, priceTag, href }: VariantCardProps) => (
  <Card className="overflow-hidden p-0 transition hover:shadow-md">
    <div className="md:hidden">
      <VariantImage variant={variant} />
      <div className="flex flex-col gap-3 p-4">
        <div className="space-y-2">
          <Typography variant="title-md" as="h2">
            {variant.name}
          </Typography>
          <BookClubDescription caption={variant.image?.caption} />
        </div>
        {priceTag && <PriceTag text={priceTag} compact />}
        <Link href={href} className="block w-full">
          <Button variant="primary" className="relative w-full" as="span">
            <LinkPendingLabel>Velg</LinkPendingLabel>
          </Button>
        </Link>
      </div>
    </div>

    <div className="hidden grid-cols-2 items-stretch md:grid">
      <div>
        <VariantImage variant={variant} />
      </div>
      <div className="flex flex-col gap-2 p-6">
        <div className="space-y-1.5">
          <Typography variant="title-md" as="h2">
            {variant.name}
          </Typography>
          <BookClubDescription caption={variant.image?.caption} />
        </div>
        <div className="mt-auto flex items-end justify-between gap-6">
          {priceTag ? <PriceTag text={priceTag} /> : <div />}
          <Link href={href} className="w-fit shrink-0">
            <Button
              variant="primary"
              className="relative w-fit shrink-0"
              as="span"
            >
              <LinkPendingLabel>Velg</LinkPendingLabel>
            </Button>
          </Link>
        </div>
      </div>
    </div>
  </Card>
);

export const VariantCardList = ({
  productId,
  variants,
}: VariantCardListProps) => {
  const [showAll, setShowAll] = React.useState(false);
  const revealAnchorRef = React.useRef<HTMLDivElement>(null);
  const initialVariants = variants.slice(0, MAX_VISIBLE_VARIANTS);
  const remainingVariants = variants.slice(MAX_VISIBLE_VARIANTS);

  React.useEffect(() => {
    if (!showAll) return;

    revealAnchorRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }, [showAll]);

  return (
    <div className="space-y-5">
      <div className="flex flex-col items-center gap-4">
        {initialVariants.map((variant) => {
          const hasGifts = variant.innmeldingsgaver.length > 0;
          const href = hasGifts
            ? `/klubb-velger/${productId}/${variant.sku}/gaver`
            : `/innmelding/${variant.sku}`;

          return (
            <div
              key={variant.sku}
              className="block w-full max-w-96 md:max-w-4xl"
            >
              <VariantCard
                variant={variant}
                href={href}
                priceTag={PRICE_TAG_PLACEHOLDER}
              />
            </div>
          );
        })}
      </div>

      {remainingVariants.length > 0 ? (
        showAll ? (
          <div
            ref={revealAnchorRef}
            className="flex flex-col items-center gap-4"
          >
            {remainingVariants.map((variant) => {
              const hasGifts = variant.innmeldingsgaver.length > 0;
              const href = hasGifts
                ? `/klubb-velger/${productId}/${variant.sku}/gaver`
                : `/innmelding/${variant.sku}`;

              return (
                <div
                  key={variant.sku}
                  className="block w-full max-w-96 md:max-w-4xl"
                >
                  <VariantCard
                    variant={variant}
                    href={href}
                    priceTag={PRICE_TAG_PLACEHOLDER}
                  />
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flex justify-center py-4">
            <ShowMoreButton onClick={() => setShowAll(true)} />
          </div>
        )
      ) : null}
    </div>
  );
};
